import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './BookingCreate.css';

const BookingCreate = () => {
  const { currentUser, canMakeBookings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preSelectedUserId = queryParams.get('userId');
  
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    userId: preSelectedUserId || '',
    propertyId: '',
    unitId: '',
    lotId: '',
    notes: ''
  });
  
  // UI state
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [bookingType, setBookingType] = useState('unit'); // 'unit' or 'lot'
  
  useEffect(() => {
    // Redirect if not authorized to make bookings
    if (!canMakeBookings()) {
      navigate('/dashboard');
      return;
    }
    
    // Load users, properties, and units/lots
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get API token from localStorage
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Fetch users assigned to this agent
        const usersRes = await axios.get('/api/users', config);
        // Filter to only get regular users
        const regularUsers = usersRes.data.filter(user => user.role === 'user');
        setUsers(regularUsers);
        
        // Fetch properties
        const propertiesRes = await axios.get('/api/properties', config);
        setProperties(propertiesRes.data);
        
        // If user was pre-selected, update form data
        if (preSelectedUserId) {
          const selectedUser = usersRes.data.find(user => user._id === preSelectedUserId);
          if (selectedUser) {
            console.log(`Pre-selected user: ${selectedUser.name}`);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load form data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [navigate, canMakeBookings, preSelectedUserId]);
  
  // When property is selected, fetch its units and lots
  useEffect(() => {
    const fetchPropertyItems = async () => {
      if (!formData.propertyId) return;
      
      try {
        // Get API token from localStorage
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Get property name for display
        const selectedProperty = properties.find(p => p._id === formData.propertyId);
        if (selectedProperty) {
          setSelectedPropertyName(selectedProperty.name);
        }
        
        // Fetch units for this property
        const unitsRes = await axios.get(
          `/api/public/properties/${formData.propertyId}/units`, 
          config
        );
        // Filter to only available units
        const availableUnits = unitsRes.data.filter(unit => 
          unit.status === 'available' || !unit.status
        );
        setUnits(availableUnits);
        
        // Fetch lots for this property (if applicable)
        try {
          const lotsRes = await axios.get(
            `/api/lots?propertyId=${formData.propertyId}`, 
            config
          );
          // Filter to only available lots
          const availableLots = lotsRes.data.filter(lot => 
            lot.status === 'available' || !lot.status
          );
          setLots(availableLots);
        } catch (err) {
          console.log('No lots available for this property');
          setLots([]);
        }
        
      } catch (err) {
        console.error('Error fetching property items:', err);
        setError('Failed to load units/lots for this property');
      }
    };
    
    fetchPropertyItems();
  }, [formData.propertyId, properties]);
  
  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Reset dependent fields when property changes
    if (name === 'propertyId') {
      setFormData({
        ...formData,
        propertyId: value,
        unitId: '',
        lotId: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Reset errors when any field changes
    setError('');
  };
  
  // Handle booking type selection
  const handleBookingTypeChange = (type) => {
    setBookingType(type);
    // Reset the other type's ID
    if (type === 'unit') {
      setFormData({ ...formData, lotId: '' });
    } else {
      setFormData({ ...formData, unitId: '' });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.userId) {
      setError('Please select a user');
      return;
    }
    
    if (!formData.propertyId) {
      setError('Please select a property');
      return;
    }
    
    if (bookingType === 'unit' && !formData.unitId) {
      setError('Please select a unit');
      return;
    }
    
    if (bookingType === 'lot' && !formData.lotId) {
      setError('Please select a lot');
      return;
    }
    
    try {
      // Get API token from localStorage
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Prepare booking data
      const bookingData = {
        userId: formData.userId,
        notes: formData.notes
      };
      
      // Add unit or lot ID based on booking type
      if (bookingType === 'unit') {
        bookingData.unitId = formData.unitId;
      } else {
        bookingData.lotId = formData.lotId;
      }
      
      // Create booking
      await axios.post('/api/bookings', bookingData, config);
      
      // Show success message and redirect
      alert('Booking created successfully');
      navigate('/agent-dashboard?tab=manage-bookings');
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.response?.data?.message || 'Failed to create booking');
    }
  };
  
  if (loading) return <div className="loading">Loading form...</div>;
  
  return (
    <div className="booking-create">
      <h2>Create Booking Request</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="booking-form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Select User:</label>
            <select 
              name="userId" 
              value={formData.userId} 
              onChange={handleChange}
              required
            >
              <option value="">-- Select User --</option>
              {users.map(user => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Select Property:</label>
            <select 
              name="propertyId" 
              value={formData.propertyId} 
              onChange={handleChange}
              required
            >
              <option value="">-- Select Property --</option>
              {properties.map(property => (
                <option key={property._id} value={property._id}>
                  {property.name}
                </option>
              ))}
            </select>
          </div>
          
          {formData.propertyId && (
            <div className="booking-type-selector">
              <div className="selector-label">Select Booking Type:</div>
              <div className="selector-buttons">
                <button 
                  type="button"
                  className={`selector-button ${bookingType === 'unit' ? 'active' : ''}`}
                  onClick={() => handleBookingTypeChange('unit')}
                >
                  Unit
                </button>
                <button 
                  type="button"
                  className={`selector-button ${bookingType === 'lot' ? 'active' : ''}`}
                  onClick={() => handleBookingTypeChange('lot')}
                >
                  Lot
                </button>
              </div>
            </div>
          )}
          
          {bookingType === 'unit' && formData.propertyId && (
            <div className="form-group">
              <label>Select Unit from {selectedPropertyName}:</label>
              {units.length === 0 ? (
                <div className="info-message">No available units found for this property.</div>
              ) : (
                <select 
                  name="unitId" 
                  value={formData.unitId} 
                  onChange={handleChange}
                  required={bookingType === 'unit'}
                >
                  <option value="">-- Select Unit --</option>
                  {units.map(unit => (
                    <option key={unit._id} value={unit._id}>
                      {unit.lotNumber || unit.unitNumber || `Unit ${unit._id.substring(0, 5)}`} 
                      {unit.type ? ` - ${unit.type}` : ''} 
                      {unit.buildUp ? ` (${unit.buildUp} sqft)` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          
          {bookingType === 'lot' && formData.propertyId && (
            <div className="form-group">
              <label>Select Lot from {selectedPropertyName}:</label>
              {lots.length === 0 ? (
                <div className="info-message">No available lots found for this property.</div>
              ) : (
                <select 
                  name="lotId" 
                  value={formData.lotId} 
                  onChange={handleChange}
                  required={bookingType === 'lot'}
                >
                  <option value="">-- Select Lot --</option>
                  {lots.map(lot => (
                    <option key={lot._id} value={lot._id}>
                      Lot {lot.lotNumber} - {lot.type || 'Standard'} 
                      {lot.buildUp ? ` (${lot.buildUp} sqft)` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          
          <div className="form-group">
            <label>Notes:</label>
            <textarea 
              name="notes" 
              value={formData.notes} 
              onChange={handleChange}
              rows="4"
              placeholder="Add any additional information or special requirements..."
            />
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Create Booking
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingCreate; 